import {Box, Grid, LogoSuite, Section, Statistic} from '@primer/react-brand/esm'
import {Logo} from '../../../components/FlexSuiteLogos/Logo'
import {renderWithoutParagraphs} from '../../../utils'
import type {IntroSectionProps} from './types'

export function IntroSection({
  introLogos,
  introLogoLink,
  introStats,
  logobarOptions,
  logoSuiteOptions,
}: IntroSectionProps) {
  const logos = renderLogos({
    introLogos,
    introLogoLink,
    logobarOptions,
    logoSuiteOptions,
  })
  const stats = renderStats({introStats})

  if (!logos && !stats) return null

  return (
    <Section fullWidth paddingBlockEnd="none" paddingBlockStart="none">
      {logos}
      {stats}
    </Section>
  )
}

function renderLogos({
  introLogos,
  introLogoLink,
  logobarOptions,
  logoSuiteOptions,
}: Pick<IntroSectionProps, 'introLogos' | 'introLogoLink' | 'logobarOptions' | 'logoSuiteOptions'>) {
  if (!introLogos || introLogos.length === 0) return null

  const takeoverButton =
    logobarOptions?.takeoverButton ??
    (introLogoLink?.fields.href ? {label: introLogoLink.fields.text, href: introLogoLink.fields.href} : undefined)

  // Primer Logobar does not support rendering the takeover CTA when marquee is enabled.
  // Ensure the CTA is never dropped by disabling marquee whenever takeoverButton is present.
  const marquee = takeoverButton ? false : logobarOptions?.marquee

  return (
    <Box borderBlockStartWidth="thin" borderColor="muted" borderStyle="solid">
      <LogoSuite align="center" variant={logoSuiteOptions?.variant}>
        <LogoSuite.Heading visuallyHidden>Logos</LogoSuite.Heading>
        <LogoSuite.Logobar
          variant={logobarOptions?.variant}
          marquee={marquee}
          marqueeSpeed={logobarOptions?.marqueeSpeed}
          takeoverButton={takeoverButton}
        >
          {introLogos.map(logo => (
            <Logo key={logo} name={logo} />
          ))}
        </LogoSuite.Logobar>
      </LogoSuite>
    </Box>
  )
}

function renderStats({introStats}: Pick<IntroSectionProps, 'introStats'>) {
  if (!introStats || introStats.length === 0) return null

  return (
    <Box marginBlockStart={{narrow: 48, regular: 80}} marginBlockEnd={{narrow: 48, regular: 80}}>
      <Grid>
        {introStats.map(stat => (
          <Grid.Column key={stat.sys.id} span={{medium: 4}}>
            <Statistic>
              <Statistic.Heading>{renderWithoutParagraphs(stat.fields.heading)}</Statistic.Heading>
              <Statistic.Description>{renderWithoutParagraphs(stat.fields.description)}</Statistic.Description>
            </Statistic>
          </Grid.Column>
        ))}
      </Grid>
    </Box>
  )
}
